import { HeaderIcons } from '../shared/icons';
import React, { useState } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { DesktopLayout, StandingsSidebar, LiveAnswer as SharedLiveAnswer, TimerSection } from '../shared';
import { ALL_GAMES } from '../../config/games';
import { usePartyHostGameLifecycle } from '../../hooks/usePartyHostGameLifecycle';
import type { GameStatistics } from '../../types/game';
import { PartyHostGameEnd } from './PartyHostShell';
import { hotTakesGameTheme } from '../../themes/cssTheme';
import '../../App.css';
import '../../styles/game-layout.css';
import { useTranslation } from '../../i18n';
import { getGameDisplayName } from '../../i18n/helpers';

interface HotTakesQuestion {
  questionId?: string;
  id?: string;
  statement: string;
  options: string[]; // ['AGREE', 'DISAGREE']
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  debatePrompt?: string;
  spicyLevel?: 1 | 2 | 3;
  category?: string;
  isHotTakes?: boolean;
}

interface VoteResultEntry {
  playerId: string;
  playerName: string;
  avatar?: string;
  vote: 'AGREE' | 'DISAGREE';
  isMinority: boolean;
  mustDrink: boolean;
}

interface DrinkerEntry {
  playerId: string;
  playerName: string;
  avatar?: string;
  vote: 'AGREE' | 'DISAGREE';
}

interface QuestionResult {
  questionId: string;
  agreeCount: number;
  disagreeCount: number;
  minorityVote: 'AGREE' | 'DISAGREE' | 'tie';
  debatePrompt?: string;
  votes?: VoteResultEntry[];
  drinkers?: DrinkerEntry[];
  spicyLevel?: number;
  category?: string;
  isHotTakes?: boolean;
}

interface LiveVote {
  playerId: string;
  playerName: string;
  avatar?: string;
  vote: 'AGREE' | 'DISAGREE';
  timestamp: number;
}

interface HotTakesHostDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: any;
    finalLeaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
    gameStatistics?: GameStatistics | null;
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
}

// Theme colors for Hot Takes — free-room orange palette; branding overrides primary/secondary
const THEME = hotTakesGameTheme;

// Helper to check if avatar is an image URL
const isAvatarImage = (avatar?: string): boolean => {
  if (!avatar) return false;
  return avatar.startsWith('/') || avatar.startsWith('data:') || avatar.includes('.svg') || avatar.includes('media');
};

// Render avatar (either as image or emoji)
const renderAvatar = (avatar?: string, size: string = '1.5rem') => {
  if (isAvatarImage(avatar)) {
    return <img src={avatar} alt="" style={{ width: size, height: size, objectFit: 'contain' }} />;
  }
  return <span style={{ fontSize: size }}>{avatar || '👤'}</span>;
};

const HotTakesHostDisplay: React.FC<HotTakesHostDisplayProps> = ({
  roomState,
  socket,
  onBackToGameSelection,
  myPlayerId,
  hostIsPlaying = false,
  onGoHome,
}) => {
  const { t } = useTranslation();
  const [hotTakesResults, setHotTakesResults] = useState<any[]>([]);
  const [selectedVote, setSelectedVote] = useState<'AGREE' | 'DISAGREE' | null>(null);
  const {
    currentQuestion,
    timeRemaining,
    questionResult,
    gameEnded,
    waitingForNewGame,
    finalLeaderboard,
    gameEndData,
    liveAnswers,
    answerSubmitted: voteSubmitted,
    playersRef,
    submitAnswer,
    restartGame,
    playSameGame,
  } = usePartyHostGameLifecycle<
    HotTakesQuestion,
    QuestionResult,
    { questionId: string; voteRecorded?: boolean; vote?: string }
  >({
    roomState,
    socket,
    hostIsPlaying,
    isQuestionForGame: (question) => !!question.isHotTakes,
    isResultForGame: (result) => !!result.isHotTakes,
    isHostAnswerAccepted: (data) => !!data.voteRecorded,
    onResetHostPlaying: () => setSelectedVote(null),
    onGameEnded: (data) => {
      const results = data.hotTakesResults;
      if (Array.isArray(results)) setHotTakesResults(results);
    },
    onAnswerReceived: (data, { addLiveAnswer }) => {
      if (!hostIsPlaying || !data.voteRecorded || !data.vote || !myPlayerId) return;
      const vote = data.vote.toUpperCase();
      if (vote !== 'AGREE' && vote !== 'DISAGREE') return;
      const player = playersRef.current.find((entry) => entry.id === myPlayerId);
      addLiveAnswer({
        playerId: myPlayerId,
        playerName: player?.name || 'Host',
        avatar: player?.avatar,
        answer: vote,
        timestamp: Date.now(),
      });
    },
  });

  const handleVoteClick = (vote: 'AGREE' | 'DISAGREE') => {
    if (voteSubmitted || !currentQuestion) return;
    setSelectedVote(vote);
    if (!submitAnswer(vote)) setSelectedVote(null);
  };

  const getGameName = getGameDisplayName;

  // Fullscreen wrapper styles
  const fullscreenStyle: React.CSSProperties = {
    minHeight: '100vh',
    width: '100%',
    background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)',
    display: 'flex',
    flexDirection: 'column',
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    padding: '2.5rem 2rem',
    paddingTop: '6rem',
    display: 'flex',
    flexDirection: 'column',
  };

  // Helper to get player avatar
  const getPlayerAvatar = (playerId: string): string | undefined => {
    const player = roomState.players.find(p => p.id === playerId);
    return player?.avatar;
  };

  // Calculate Hot Takes statistics
  const calculateHotTakesStatistics = () => {
    if (!hotTakesResults || hotTakesResults.length === 0) return null;

    // Calculate drinking stats per player
    const playerDrinks = new Map<string, number>();
    const playerMinorityCount = new Map<string, number>();
    let totalTies = 0;
    let mostControversial: { statement: string; agreeCount: number; disagreeCount: number } | null = null;
    let maxDifference = 0;

    hotTakesResults.forEach(result => {
      if (result.minorityVote === 'tie') {
        totalTies++;
      }

      // Track controversy (closest to 50/50 split)
      const difference = Math.abs(result.agreeCount - result.disagreeCount);
      if (difference < maxDifference || mostControversial === null) {
        maxDifference = difference;
        mostControversial = {
          statement: result.statement,
          agreeCount: result.agreeCount,
          disagreeCount: result.disagreeCount,
        };
      }

      result.votes.forEach((vote: VoteResultEntry) => {
        if (vote.mustDrink) {
          playerDrinks.set(vote.playerId, (playerDrinks.get(vote.playerId) || 0) + 1);
          if (vote.isMinority) {
            playerMinorityCount.set(vote.playerId, (playerMinorityCount.get(vote.playerId) || 0) + 1);
          }
        }
      });
    });

    // Find player who drank most
    let mostDrinks: { playerId: string; playerName: string; count: number } | null = null;
    playerDrinks.forEach((count, playerId) => {
      const player = roomState.players.find(p => p.id === playerId);
      if (player && (!mostDrinks || count > mostDrinks.count)) {
        mostDrinks = {
          playerId,
          playerName: player.name,
          count,
        };
      }
    });

    return {
      totalQuestions: hotTakesResults.length,
      totalTies,
      mostDrinks,
      mostControversial,
      playerDrinks: Array.from(playerDrinks.entries()).map(([playerId, count]) => {
        const player = roomState.players.find(p => p.id === playerId);
        return {
          playerId,
          playerName: player?.name || 'Unknown',
          count,
        };
      }),
    };
  };

  const hotTakesStats = calculateHotTakesStatistics();

  // Create custom game statistics for GameEndScreen
  const gameStatistics = hotTakesStats ? {
    totalQuestions: hotTakesStats.totalQuestions,
    averageScore: 0, // Hot Takes doesn't use scores
    mostCorrectAnswers: undefined,
    wrongAnswers: [],
    hotTakesStats: hotTakesStats,
    hotTakesResults: hotTakesResults,
  } : null;

  // Game ended screen
  if (gameEnded) {
    return (
      <PartyHostGameEnd
        roomName={roomState.roomName}
        gameType={roomState.gameType}
        finalLeaderboard={finalLeaderboard}
        gameStatistics={gameStatistics}
        gameEndData={gameEndData}
        onPlaySameGame={playSameGame}
        onBackToGameSelection={onBackToGameSelection}
        getPlayerAvatar={getPlayerAvatar}
      />
    );
  }

  // Waiting state
  if (waitingForNewGame || (!currentQuestion && !questionResult)) {
    return (
      <div style={fullscreenStyle}>
        <Header
          leftButton={onBackToGameSelection ? {
            icon: HeaderIcons.back,
            onClick: onBackToGameSelection,
            tooltip: t('gameplay.backToGameSelection')
          } : undefined}
          rightButton={{
            icon: HeaderIcons.refresh,
            onClick: () => window.location.reload(),
            tooltip: t('common.tooltips.refreshPage')
          }}
          centerContent={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div className="header-game-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('gameplay.partyHost.gameLabel')}</span>
                <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#F8FAFC' }}>
                  {getGameName(roomState.gameType)}
                </span>
              </div>
              <div className="header-game-name" style={{ 
                width: '1px', 
                height: '24px', 
                backgroundColor: 'rgba(255, 255, 255, 0.3)' 
              }}></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('gameplay.partyHost.roomCodeLabel')}</span>
                <div style={{ 
                  fontSize: '1.3rem', 
                  fontWeight: 'bold', 
                  letterSpacing: '0.15rem',
                  color: THEME.primary,
                  padding: '0.3rem 0.8rem',
                  backgroundColor: THEME.primaryLight,
                  borderRadius: '6px',
                  border: `2px solid ${THEME.primaryBorder}`,
                }}>
                  {roomState.roomName}
                </div>
              </div>
            </div>
          }
        />
        <div style={{ ...contentStyle, alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>🔥</div>
          <h2 style={{ fontSize: '2rem' }}>{t('gameplay.hotTakes.getReady')}</h2>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <span className="loading-dot" style={{ fontSize: '2rem', animationDelay: '0s' }}>🌶️</span>
            <span className="loading-dot" style={{ fontSize: '2rem', animationDelay: '0.2s' }}>🔥</span>
            <span className="loading-dot" style={{ fontSize: '2rem', animationDelay: '0.4s' }}>🌶️</span>
          </div>
        </div>
        <style>{`
          .loading-dot { display: inline-block; animation: bounce 0.6s infinite alternate; }
          @keyframes bounce { from { transform: translateY(0); } to { transform: translateY(-15px); } }
        `}</style>
      </div>
    );
  }

  // Question result screen - fullscreen showing vote breakdown
  if (questionResult) {
    const drinkers = questionResult.drinkers || [];
    const isTie = questionResult.minorityVote === 'tie';
    const totalVotes = questionResult.agreeCount + questionResult.disagreeCount;

    return (
      <div style={fullscreenStyle}>
        <Header
          leftButton={onBackToGameSelection ? {
            icon: HeaderIcons.back,
            onClick: onBackToGameSelection,
            tooltip: t('gameplay.backToGameSelection')
          } : undefined}
          rightButton={{
            icon: HeaderIcons.refresh,
            onClick: () => window.location.reload(),
            tooltip: t('common.tooltips.refreshPage')
          }}
          centerContent={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('gameplay.partyHost.gameLabel')}</span>
                <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#F8FAFC' }}>
                  {getGameName(roomState.gameType)}
                </span>
              </div>
              <div style={{ 
                width: '1px', 
                height: '24px', 
                backgroundColor: 'rgba(255, 255, 255, 0.3)' 
              }}></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('gameplay.partyHost.roomCodeLabel')}</span>
                <div style={{ 
                  fontSize: '1.3rem', 
                  fontWeight: 'bold', 
                  letterSpacing: '0.15rem',
                  color: THEME.primary,
                  padding: '0.3rem 0.8rem',
                  backgroundColor: THEME.primaryLight,
                  borderRadius: '6px',
                  border: `2px solid ${THEME.primaryBorder}`,
                }}>
                  {roomState.roomName}
                </div>
              </div>
            </div>
          }
        />
        <div style={contentStyle}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%',
          }}>
            {/* Vote Count Display */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '4rem',
              marginBottom: '2rem',
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '4rem', fontWeight: 'bold', color: THEME.agree, lineHeight: 1 }}>
                  {questionResult.agreeCount}
                </div>
                <div style={{ fontSize: '1.5rem', color: THEME.agree, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  👍 AGREE
                </div>
                <div style={{ fontSize: '1rem', opacity: 0.6, marginTop: '0.25rem' }}>
                  {totalVotes > 0 ? Math.round((questionResult.agreeCount / totalVotes) * 100) : 0}%
                </div>
              </div>
              <div style={{ fontSize: '3rem', alignSelf: 'center', opacity: 0.3 }}>VS</div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '4rem', fontWeight: 'bold', color: THEME.accent, lineHeight: 1 }}>
                  {questionResult.disagreeCount}
                </div>
                <div style={{ fontSize: '1.5rem', color: THEME.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  👎 DISAGREE
                </div>
                <div style={{ fontSize: '1rem', opacity: 0.6, marginTop: '0.25rem' }}>
                  {totalVotes > 0 ? Math.round((questionResult.disagreeCount / totalVotes) * 100) : 0}%
                </div>
              </div>
            </div>

            {/* Vote Distribution Bar */}
            <div style={{
              display: 'flex',
              height: '24px',
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '2rem',
              background: 'rgba(255,255,255,0.1)',
            }}>
              <div style={{
                width: `${totalVotes > 0 ? (questionResult.agreeCount / totalVotes) * 100 : 50}%`,
                background: THEME.agree,
                transition: 'width 0.5s ease-out',
              }} />
              <div style={{
                width: `${totalVotes > 0 ? (questionResult.disagreeCount / totalVotes) * 100 : 50}%`,
                background: THEME.accent,
                transition: 'width 0.5s ease-out',
              }} />
            </div>

            {/* Vote Lists */}
            <div className="votes-grid" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '2rem',
              marginBottom: '2rem',
            }}>
              {/* Agree Voters */}
              <div style={{
                background: THEME.agreeLight,
                border: `3px solid ${THEME.agreeBorder}`,
                borderRadius: '1.5rem',
                padding: '1.5rem',
              }}>
                <h3 style={{ textAlign: 'center', color: THEME.agree, marginBottom: '1rem', fontSize: '1.3rem' }}>
                  👍 Agreed ({questionResult.agreeCount})
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {questionResult.votes?.filter(v => v.vote === 'AGREE').map((voter) => (
                    <div key={voter.playerId} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: voter.mustDrink ? 'rgba(239, 68, 68, 0.2)' : 'rgba(0,0,0,0.2)',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.5rem',
                      border: voter.mustDrink ? '2px solid rgba(239, 68, 68, 0.5)' : 'none',
                    }}>
                      {renderAvatar(voter.avatar, '1.5rem')}
                      <span>{voter.playerName}</span>
                      {voter.mustDrink && <span>🍺</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Disagree Voters */}
              <div style={{
                background: THEME.accentLight,
                border: `3px solid ${THEME.accentBorder}`,
                borderRadius: '1.5rem',
                padding: '1.5rem',
              }}>
                <h3 style={{ textAlign: 'center', color: THEME.accent, marginBottom: '1rem', fontSize: '1.3rem' }}>
                  👎 Disagreed ({questionResult.disagreeCount})
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {questionResult.votes?.filter(v => v.vote === 'DISAGREE').map((voter) => (
                    <div key={voter.playerId} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: voter.mustDrink ? 'rgba(239, 68, 68, 0.2)' : 'rgba(0,0,0,0.2)',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.5rem',
                      border: voter.mustDrink ? '2px solid rgba(239, 68, 68, 0.5)' : 'none',
                    }}>
                      {renderAvatar(voter.avatar, '1.5rem')}
                      <span>{voter.playerName}</span>
                      {voter.mustDrink && <span>🍺</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Who Drinks Section */}
            <div className="drinking-section" style={{
              background: drinkers.length > 0 ? THEME.primaryLight : THEME.agreeLight,
              border: `3px solid ${drinkers.length > 0 ? THEME.primaryBorder : THEME.agreeBorder}`,
              borderRadius: '1.5rem',
              padding: '2rem',
              paddingBottom: '5rem',
              textAlign: 'center',
              marginBottom: '2rem',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <h2 style={{
                color: drinkers.length > 0 ? THEME.primary : THEME.agree,
                marginBottom: '1.5rem',
                fontSize: '2rem',
              }}>
                {isTie ? '🍺 TIE! Everyone Drinks! 🍺' : 
                 drinkers.length > 0 ? '🍺 Minority Drinks! 🍺' : 
                 '🎉 Perfect Split! Nobody Drinks! 🎉'}
              </h2>
              
              {drinkers.length > 0 && (
                <div className="drinkers-list" style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: '1rem',
                  marginBottom: '3rem',
                }}>
                  {drinkers.map((drinker, index) => (
                    <div key={drinker.playerId} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      background: 'rgba(0,0,0,0.3)',
                      padding: '1rem 1.5rem',
                      borderRadius: '1rem',
                      animation: `slideIn 0.3s ease-out ${index * 0.1}s`,
                    }}>
                      {renderAvatar(drinker.avatar, '2.5rem')}
                      <span style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>{drinker.playerName}</span>
                      <span style={{ fontSize: '2rem' }}>🍺</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Animated beer ticker at bottom - like DrinkingGame */}
              <div style={{
                position: 'absolute',
                bottom: '0.5rem',
                left: 0,
                right: 0,
                height: '4.5rem',
                overflow: 'hidden',
              }}>
                <div className="beer-ticker" style={{
                  display: 'flex',
                  width: 'max-content',
                  animation: 'beerTicker 20s linear infinite',
                }}>
                  {/* First set of emojis */}
                  {['🍺', '🍻', '🥂', '🍺', '🥃', '🍷', '🍺', '🍻', '🍺', '🥂', '🍾', '🍺', '🍻', '🥃', '🍺', '🍷', '🍺', '🍻', '🥂', '🍺'].map((emoji, i) => (
                    <span 
                      key={`a-${i}`}
                      className="beer-bounce" 
                      style={{ 
                        fontSize: '2rem', 
                        padding: '0 1rem',
                        animationDelay: `${i * 0.15}s`,
                      }}
                    >
                      {emoji}
                    </span>
                  ))}
                  {/* Duplicate set for seamless loop */}
                  {['🍺', '🍻', '🥂', '🍺', '🥃', '🍷', '🍺', '🍻', '🍺', '🥂', '🍾', '🍺', '🍻', '🥃', '🍺', '🍷', '🍺', '🍻', '🥂', '🍺'].map((emoji, i) => (
                    <span 
                      key={`b-${i}`}
                      className="beer-bounce" 
                      style={{ 
                        fontSize: '2rem', 
                        padding: '0 1rem',
                        animationDelay: `${(i + 20) * 0.15}s`,
                      }}
                    >
                      {emoji}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Debate Prompt */}
            {questionResult.debatePrompt && (
              <div style={{
                background: THEME.primaryLight,
                border: `3px solid ${THEME.primaryBorder}`,
                borderRadius: '1.5rem',
                padding: '1.5rem',
                textAlign: 'center',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.75rem',
                  fontWeight: 'bold',
                  color: THEME.primary,
                  fontSize: '1.3rem',
                }}>
                  <span>💬</span>
                  <span>{t('gameplay.hotTakes.debateTime')}</span>
                </div>
                <div style={{ fontSize: '1.2rem', lineHeight: '1.6', fontStyle: 'italic' }}>
                  {questionResult.debatePrompt}
                </div>
              </div>
            )}
          </div>
        </div>
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateX(-20px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes beerTicker {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          @keyframes beerBounce {
            0%, 100% { transform: translateY(0) rotate(0deg); }
            25% { transform: translateY(-12px) rotate(-8deg); }
            75% { transform: translateY(-12px) rotate(8deg); }
          }
          .beer-bounce {
            display: inline-block;
            animation: beerBounce 0.5s ease-in-out infinite;
          }
          .beer-ticker {
            will-change: transform;
          }
          @media (max-width: 768px) {
            .votes-grid { grid-template-columns: 1fr !important; }
            .drinking-section { padding: 1.5rem !important; padding-bottom: 5rem !important; }
            .drinkers-list { gap: 0.5rem !important; }
          }
        `}</style>
      </div>
    );
  }

  // Question display - fullscreen with live vote columns
  const progress = currentQuestion ? (timeRemaining / currentQuestion.timeLimit) * 100 : 0;
  const secondsRemaining = Math.ceil(timeRemaining / 1000);

  // Convert liveVotes to shared format
  const sharedLiveAnswers: SharedLiveAnswer[] = liveAnswers.map(answer => ({
    playerId: answer.playerId,
    playerName: answer.playerName,
    avatar: answer.avatar,
    timestamp: answer.timestamp,
    answer: answer.answer,
  }));

  return (
    <div className="app">
      <Header
        leftButton={{
          icon: HeaderIcons.back,
          onClick: onBackToGameSelection || (() => {}),
          tooltip: t('gameplay.backToGameSelection')
        }}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: restartGame,
          tooltip: t('gameplay.restartGame')
        }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div className="header-game-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('gameplay.partyHost.gameLabel')}</span>
              <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#F8FAFC' }}>
                🔥 Hot Takes
              </span>
            </div>
            <div className="header-game-name" style={{ 
              width: '1px', 
              height: '24px', 
              backgroundColor: 'rgba(255, 255, 255, 0.3)' 
            }}></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('gameplay.partyHost.roomCodeLabel')}</span>
              <div style={{ 
                fontSize: '1.3rem', 
                fontWeight: 'bold', 
                letterSpacing: '0.15rem',
                color: THEME.primary,
                padding: '0.3rem 0.8rem',
                backgroundColor: THEME.primaryLight,
                borderRadius: '6px',
                border: `2px solid ${THEME.primaryBorder}`,
              }}>
                {roomState.roomName}
              </div>
            </div>
          </div>
        }
      />

      {/* Main game layout - desktop: left (question) | right (standings), mobile: stacked */}
      <div style={{
        minHeight: 'calc(100vh - 82px)',
        width: '100%',
        padding: '1rem',
        paddingTop: '1rem',
        paddingBottom: '1rem',
      }}>
        <DesktopLayout
          leftContent={
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Main Challenge Display */}
        <div 
          className="container game-display-container" 
          style={{ 
            paddingTop: '1rem',
            paddingBottom: '1rem',
            marginTop: '1rem',
            marginBottom: 0,
          }}
        >
          <div className="timer-section" style={{ width: '100%', marginBottom: '1.2rem' }}>
            <TimerSection
              questionNumber={currentQuestion?.questionNumber}
              totalQuestions={currentQuestion?.totalQuestions}
              timeSeconds={secondsRemaining}
              progress={progress}
              primaryColor={THEME.primary}
            />
          </div>

          {/* Question text - using same font size as trivia game */}
          <h1 className="game-question-text" style={{
            color: THEME.primary,
            marginBottom: '1.5rem',
            letterSpacing: '0.03em',
            textShadow: `0 2px 8px ${THEME.primaryBorder}`,
          }}>
            "{currentQuestion?.statement}"
          </h1>

          {/* Vote Options (if host is playing) */}
          {hostIsPlaying && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '1rem',
              marginTop: '1rem',
            }}>
              <button
                onClick={() => handleVoteClick('AGREE')}
                disabled={voteSubmitted}
                className="button"
                style={{
                  padding: '1.5rem',
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  cursor: voteSubmitted ? 'default' : 'pointer',
                  transition: 'all 0.2s',
                  border: selectedVote === 'AGREE' ? `3px solid ${THEME.agree}` : '2px solid rgba(255,255,255,0.2)',
                  background: selectedVote === 'AGREE' ? THEME.agreeLight : 'rgba(255,255,255,0.05)',
                  color: selectedVote === 'AGREE' ? THEME.agree : 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <span style={{ fontSize: '2rem' }}>👍</span>
                AGREE
              </button>
              <button
                onClick={() => handleVoteClick('DISAGREE')}
                disabled={voteSubmitted}
                className="button"
                style={{
                  padding: '1.5rem',
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  cursor: voteSubmitted ? 'default' : 'pointer',
                  transition: 'all 0.2s',
                  border: selectedVote === 'DISAGREE' ? `3px solid ${THEME.accent}` : '2px solid rgba(255,255,255,0.2)',
                  background: selectedVote === 'DISAGREE' ? THEME.accentLight : 'rgba(255,255,255,0.05)',
                  color: selectedVote === 'DISAGREE' ? THEME.accent : 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <span style={{ fontSize: '2rem' }}>👎</span>
                DISAGREE
              </button>
            </div>
          )}

          {/* Host Playing Feedback */}
          {hostIsPlaying && voteSubmitted && (
            <div style={{
              background: THEME.primaryLight,
              border: `3px solid ${THEME.primaryBorder}`,
              borderRadius: '12px',
              padding: '1rem',
              textAlign: 'center',
              marginTop: '1rem',
            }}>
              <div style={{ fontSize: '1.1rem', color: THEME.primary }}>
                {t('gameplay.hotTakes.youVotedWaiting', { vote: selectedVote })}
              </div>
            </div>
          )}
        </div>
            </div>
          }
          rightContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion?.questionId || currentQuestion?.id}
              shouldUpdateScores={false}
              theme={{
                primary: THEME.primary,
                primaryLight: THEME.primaryLight,
                primaryBorder: THEME.primaryBorder,
                accent: THEME.tertiary,
                accentLight: THEME.tertiaryLight,
                accentBorder: THEME.tertiaryBorder,
              }}
            />
          }
          mobileOnlyContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion?.questionId || currentQuestion?.id}
              shouldUpdateScores={false}
              isMobile={true}
              theme={{
                primary: THEME.primary,
                primaryLight: THEME.primaryLight,
                primaryBorder: THEME.primaryBorder,
                accent: THEME.tertiary,
                accentLight: THEME.tertiaryLight,
                accentBorder: THEME.tertiaryBorder,
              }}
            />
          }
          rightWidth="320px"
        />
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
        @media (max-width: 1024px) {
          .timer-display { font-size: 2.5rem !important; }
          .question-card { padding: 2rem !important; }
          .question-text { font-size: 1.8rem !important; }
          .option-button { padding: 1rem !important; font-size: 1.1rem !important; }
          .question-progress { font-size: 0.95rem !important; }
        }
        @media (max-width: 600px) {
          .timer-display { font-size: 2rem !important; }
          .question-card { padding: 1.5rem !important; }
          .question-text { font-size: 1.4rem !important; }
          .option-button { padding: 0.875rem !important; font-size: 1rem !important; }
        }
      `}</style>
    </div>
  );
};

export default HotTakesHostDisplay;
